import React, { useEffect, useState } from 'react';
import { Loader2, ChevronDown, ChevronUp, ArrowRight, AlertTriangle, Wand2 } from 'lucide-react';
import type { Question, AnswerType, AutoFixPreviewSummary, SectionInfo } from './types';
import SeverityBadge from './SeverityBadge';
import AutoFixForm from './AutoFixForm';

interface QuestionCardProps {
  question: Question;
  onApply: (questionId: string, optionId: string, params?: Record<string, any>) => Promise<void>;
  onPreview?: (questionId: string, optionId: string, params: Record<string, any>) => Promise<{ rowsChanged: number; total: number; summaryAction: string | null } | null>;
  onNavigateToIssue: (issueId: string) => void;
  isApplying: boolean;
  applyError: string | null;
  availableColumns?: string[];
  sectionsByFile?: Record<string, SectionInfo[]>;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  onApply,
  onPreview,
  onNavigateToIssue,
  isApplying,
  applyError,
  availableColumns = [],
  sectionsByFile = {},
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(question.selected_option_id || null);
  const [textValue, setTextValue] = useState('');
  const [multiSelected, setMultiSelected] = useState<Set<string>>(new Set());
  const [detailExpanded, setDetailExpanded] = useState(false);
  const [autoFixParams, setAutoFixParams] = useState<Record<string, any>>({});
  const [previewSummary, setPreviewSummary] = useState<AutoFixPreviewSummary | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const isApplied = question.applied || question.status === 'APPLIED';
  const isAnswered = selectedOption !== null && !isApplied;
  const selectedOpt = question.options.find(o => o.option_id === selectedOption);
  const isAutoFix = !!selectedOpt?.option_id && (
    selectedOpt.option_id.startsWith('AUTO_FIX_') || selectedOpt.option_id.startsWith('PROSE_FIX_')
  );
  const columns = availableColumns.length > 0 ? availableColumns : (question.available_columns || []);
  const questionScope = {
    sectionRefs: question.section_refs || [],
    files: question.files || [],
  };

  const isProseQuestion = question.question_type === 'Q_DOCUMENT_RECONCILIATION';
  const RESOLUTION_KIND_TO_OPTION: Record<string, string> = {
    pick_winner: 'PROSE_FIX_PICK_WINNER',
    edit_text: 'PROSE_FIX_EDIT_SECTION',
    edit_section: 'PROSE_FIX_EDIT_SECTION',
    fill_gap: 'PROSE_FIX_FILL_GAP',
    merge_clauses: 'PROSE_FIX_MERGE_CLAUSES',
    flag: 'PROSE_FIX_FLAG_FOR_REVIEW',
    acknowledge: 'PROSE_FIX_ACKNOWLEDGE',
  };
  const recommendedOptionIds = (() => {
    if (!isProseQuestion) return new Set<string>();
    const cres = question.candidate_resolutions || [];
    const ids = new Set<string>();
    for (const cr of cres) {
      const mapped = RESOLUTION_KIND_TO_OPTION[String(cr.kind || '').toLowerCase()];
      if (mapped) ids.add(mapped);
    }
    if (ids.size === 0 && question.options.length > 0) {
      ids.add(question.options[0].option_id);
    }
    return ids;
  })();
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const recommendedOptions = isProseQuestion
    ? question.options.filter(o => recommendedOptionIds.has(o.option_id))
    : question.options;
  const moreOptions = isProseQuestion
    ? question.options.filter(o => !recommendedOptionIds.has(o.option_id))
    : [];
  const visibleOptions = isProseQuestion && !showMoreOptions
    ? recommendedOptions
    : question.options;

  useEffect(() => {
    setPreviewSummary(null);
    setPreviewError(null);
  }, [selectedOption]);

  const handlePreview = async (params: Record<string, any>) => {
    if (!onPreview || !selectedOption) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const summary = await onPreview(question.question_id, selectedOption, params);
      setPreviewSummary(summary || { rowsChanged: 0, total: 0, summaryAction: null });
    } catch (err: any) {
      setPreviewError(err?.message || 'Preview failed.');
    } finally {
      setPreviewLoading(false);
    }
  };

  if (isApplied) {
    return (
      <div className="border border-[#A7F3D0] rounded-xl overflow-hidden bg-white border-l-4 border-l-[#059669]" id={`question-${question.question_id}`}>
        <div className="px-5 py-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#D1FAE5] flex items-center justify-center text-sm font-bold text-[#065F46]">
                {question.question_id.replace(/\D/g, '').slice(-2) || '?'}
              </div>
              <div>
                <p className="text-[14px] font-semibold text-[#0F172A]">{question.question_text}</p>
                <p className="text-[12px] text-[#64748B] mt-0.5">{question.records_affected} records affected</p>
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#D1FAE5] text-[#065F46] whitespace-nowrap">
              &#10003; Applied
            </span>
          </div>

          <div className="mt-3 bg-[#F0FDF4] border border-[#A7F3D0] rounded-lg px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[13px] text-[#065F46]">
                  <span className="font-semibold">Selected:</span> {selectedOpt?.label || question.selected_option_id}
                </p>
                {question.applied_at && (
                  <p className="text-[11px] text-[#059669] mt-1">
                    Applied by {question.applied_by || 'user'} &middot; {new Date(question.applied_at).toLocaleString()}
                  </p>
                )}
              </div>
              <button onClick={() => setDetailExpanded(!detailExpanded)} className="text-[#059669] hover:text-[#047857]">
                {detailExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
            {question.linked_issue_id && (
              <button
                onClick={() => onNavigateToIssue(question.linked_issue_id!)}
                className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-[#0D9488] hover:text-[#0B7A70]"
              >
                Issue #{question.linked_issue_id} &mdash; Resolved <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>

          {detailExpanded && (
            <div className="mt-3 text-[12px] text-[#64748B] space-y-1">
              <p><span className="font-semibold">Context:</span> {question.business_context}</p>
              <p><span className="font-semibold">Columns:</span> {question.columns_affected.join(', ')}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white border-l-4 border-l-[#F59E0B]" id={`question-${question.question_id}`}>
      <div className="px-5 py-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#FEF3C7] flex items-center justify-center text-sm font-bold text-[#92400E]">
              {question.question_id.replace(/\D/g, '').slice(-2) || '?'}
            </div>
            <div>
              <p className="text-[14px] font-semibold text-[#0F172A]">{question.question_text}</p>
              <p className="text-[12px] text-[#64748B] mt-1 leading-relaxed">{question.business_context}</p>
            </div>
          </div>
          <SeverityBadge severity={question.severity} />
        </div>

        <div className="flex flex-wrap gap-1.5 mb-2">
          {question.columns_affected.map(col => (
            <span key={col} className="px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] text-[11px] font-mono rounded">
              {col}
            </span>
          ))}
          <span className="text-[12px] text-[#64748B] self-center ml-1">{question.records_affected} records affected</span>
        </div>

        <div className="mt-4 space-y-2">
          {isProseQuestion && recommendedOptions.length > 0 && !showMoreOptions && (
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#5B21B6]">
              Recommended approach{recommendedOptions.length === 1 ? '' : 'es'}
            </div>
          )}
          <AnswerInput
            answerType={question.answer_type}
            options={visibleOptions}
            selectedOption={selectedOption}
            onSelect={setSelectedOption}
            textValue={textValue}
            onTextChange={setTextValue}
            multiSelected={multiSelected}
            onMultiToggle={(id) => {
              const next = new Set(multiSelected);
              if (next.has(id)) next.delete(id); else next.add(id);
              setMultiSelected(next);
              setSelectedOption(next.size > 0 ? Array.from(next).join(',') : null);
            }}
            disabled={isApplied}
          />
          {isProseQuestion && moreOptions.length > 0 && (
            <button
              type="button"
              onClick={() => setShowMoreOptions(v => !v)}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#7C3AED] hover:text-[#5B21B6] mt-1"
            >
              {showMoreOptions ? (
                <>
                  <ChevronUp className="w-3.5 h-3.5" /> Hide other options
                </>
              ) : (
                <>
                  <ChevronDown className="w-3.5 h-3.5" /> Show {moreOptions.length} more option{moreOptions.length === 1 ? '' : 's'}
                </>
              )}
            </button>
          )}
        </div>

        {isAnswered && selectedOpt && isAutoFix && (
          <div className="mt-3 animate-in fade-in duration-200">
            <div className="flex items-center gap-1.5 mb-2 text-[12px] text-[#0D9488]">
              <Wand2 className="w-3.5 h-3.5" />
              <span className="font-semibold">Configure auto-fix rule</span>
            </div>
            <AutoFixForm
              option={selectedOpt}
              availableColumns={columns}
              initialParams={autoFixParams}
              onParamsChange={setAutoFixParams}
              onPreview={handlePreview}
              previewSummary={previewSummary}
              previewLoading={previewLoading}
              previewError={previewError}
              disabled={isApplied || isApplying}
              sectionsByFile={sectionsByFile}
              questionScope={questionScope}
            />
          </div>
        )}

        {isAnswered && selectedOpt && !isAutoFix && (
          <div className="mt-3 bg-[#FEF3C7] border border-[#FDE68A] rounded-lg px-4 py-2.5 animate-in fade-in duration-200">
            <p className="text-[12px] text-[#92400E]">
              <span className="font-semibold">If you apply this:</span> {selectedOpt.preview_statement}
            </p>
          </div>
        )}

        {!isAnswered && (
          <p className="mt-3 text-[11px] text-[#94A3B8] leading-relaxed">
            <span className="font-semibold">If left unanswered:</span> {question.unanswered_consequence || 'These records will remain flagged and unresolved in your output.'}
          </p>
        )}

        {applyError && (
          <div className="mt-3 bg-[#FEE2E2] border border-[#FECACA] rounded-lg px-3 py-2">
            <p className="text-[12px] text-[#991B1B] flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              {applyError}
            </p>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          {isAnswered && (
            <button
              onClick={() => setSelectedOption(null)}
              className="text-[12px] text-[#64748B] hover:text-[#0F172A] underline"
            >
              Change selection
            </button>
          )}
          <div className="ml-auto">
            <button
              onClick={() => selectedOption && onApply(question.question_id, selectedOption, isAutoFix ? autoFixParams : undefined)}
              disabled={!selectedOption || isApplying}
              className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-[13px] font-semibold transition-all ${
                selectedOption && !isApplying
                  ? 'bg-[#0D9488] text-white hover:bg-[#0B7A70] shadow-sm hover:-translate-y-0.5'
                  : 'bg-[#E2E8F0] text-[#94A3B8] cursor-not-allowed'
              }`}
            >
              {isApplying ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying...</> : isAutoFix ? 'Apply auto-fix' : 'Apply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AnswerInput: React.FC<{
  answerType: AnswerType;
  options: Question['options'];
  selectedOption: string | null;
  onSelect: (id: string) => void;
  textValue: string;
  onTextChange: (v: string) => void;
  multiSelected: Set<string>;
  onMultiToggle: (id: string) => void;
  disabled: boolean;
}> = ({ answerType, options, selectedOption, onSelect, textValue, onTextChange, multiSelected, onMultiToggle, disabled }) => {
  if (answerType === 'YES_NO') {
    return (
      <div className="flex gap-3">
        {options.map(opt => (
          <button
            key={opt.option_id}
            onClick={() => !disabled && onSelect(opt.option_id)}
            className={`flex-1 py-3 px-4 rounded-lg border-[1.5px] text-[13px] font-semibold transition-all ${
              selectedOption === opt.option_id
                ? 'border-[#0D9488] bg-[#F0FDFA] text-[#0D9488]'
                : 'border-[#E2E8F0] bg-white text-[#0F172A] hover:border-[#0D9488] hover:bg-[#F0FDFA]'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  if (answerType === 'TEXT_INPUT') {
    return (
      <div>
        {options.length > 0 && <p className="text-[12px] text-[#64748B] mb-2">{options[0].description}</p>}
        <input
          type="text"
          value={textValue}
          onChange={(e) => { onTextChange(e.target.value); if (e.target.value.trim()) onSelect(e.target.value.trim()); }}
          placeholder="Enter value..."
          disabled={disabled}
          className="w-full px-3 py-2 text-[13px] border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#0D9488] focus:border-[#0D9488] outline-none"
        />
      </div>
    );
  }

  if (answerType === 'MULTI_CHOICE') {
    return (
      <div className="space-y-2">
        {options.map(opt => (
          <label
            key={opt.option_id}
            onClick={() => !disabled && onMultiToggle(opt.option_id)}
            className={`flex items-start gap-3 p-3 rounded-lg border-[1.5px] cursor-pointer transition-all ${
              multiSelected.has(opt.option_id)
                ? 'border-[#0D9488] bg-[#F0FDFA]'
                : 'border-[#E2E8F0] hover:border-[#0D9488] hover:bg-[#F0FDFA]'
            }`}
          >
            <div className={`w-4 h-4 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
              multiSelected.has(opt.option_id) ? 'bg-[#0D9488] border-[#0D9488]' : 'border-[#CBD5E1]'
            }`}>
              {multiSelected.has(opt.option_id) && (
                <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-white"><path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
              )}
            </div>
            <div>
              <p className="text-[13px] font-medium text-[#0F172A]">{opt.label}</p>
              {opt.description && <p className="text-[11px] text-[#64748B] mt-0.5">{opt.description}</p>}
            </div>
          </label>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {options.map(opt => (
        <label
          key={opt.option_id}
          onClick={() => !disabled && onSelect(opt.option_id)}
          className={`flex items-start gap-3 p-3 rounded-lg border-[1.5px] cursor-pointer transition-all ${
            selectedOption === opt.option_id
              ? 'border-[#0D9488] bg-[#F0FDFA]'
              : 'border-[#E2E8F0] hover:border-[#0D9488] hover:bg-[#F0FDFA]'
          }`}
        >
          <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            selectedOption === opt.option_id ? 'border-[#0D9488]' : 'border-[#CBD5E1]'
          }`}>
            {selectedOption === opt.option_id && <div className="w-2 h-2 rounded-full bg-[#0D9488]" />}
          </div>
          <div>
            <p className="text-[13px] font-medium text-[#0F172A]">{opt.label}</p>
            {opt.description && <p className="text-[11px] text-[#64748B] mt-0.5">{opt.description}</p>}
          </div>
        </label>
      ))}
    </div>
  );
};

export default QuestionCard;
